// The shared vision panel (056 vision-screen re-housing: owner-approved
// redesign, 2026-07-09; specs/056-vision-capture-method/redesign-brief.md).
// One anatomy for every act on the board, composed FROM the house idioms:
// the SetupPanel card (section[aria-labelledby], white card on the slate
// page, head row + right count pill) and its collapse-to-strip (a done act
// renders as one line: tick + label + summary + right link).
//
// PRESENTATION ONLY. Expansion DERIVES from the delivered state
// (defaultExpanded, computed by VisionSurface from the active act); the
// chevron / the strip's Review link override it as LOCAL VIEW-STATE only.
// Sealed sessions render the staged shapes all static: no chevron, no
// Review, the derived default wins. State is carried by text + icon, never
// colour alone.

import React, { useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import '../../styles/VisionPanel.css';

export interface VisionPanelStrip {
  /** The strip's one-line label (e.g. "Challenges", "Direction"). */
  label: string;
  /** The strip's summary: a count, the crux tag, a quoted sentence. */
  summary?: React.ReactNode;
  /** A completed act's strip carries the brand tick; an empty collapsed
   * panel (Benefits before anything lands) carries a quiet chevron glyph. */
  done?: boolean;
}

export interface VisionPanelProps {
  /** The stable id base for aria-labelledby. */
  panelId: string;
  testId?: string;
  /** The visible panel title (Where you are / Challenges / Direction /
   * Benefits: plain words, never method words). */
  title: string;
  subLine?: string;
  /** The right-hand count pill text (e.g. "3 · 1 crux", "1 of 2 complete"). */
  countPill?: string;
  sealed: boolean;
  /** The active act carries the brand-soft head tint. */
  active?: boolean;
  /** Derived purely from the delivered state by the surface. */
  defaultExpanded: boolean;
  /** The collapsed rendering. Without it the panel never collapses. */
  strip?: VisionPanelStrip;
  children: React.ReactNode;
}

const VisionPanel: React.FC<VisionPanelProps> = ({
  panelId,
  testId,
  title,
  subLine,
  countPill,
  sealed,
  active = false,
  defaultExpanded,
  strip,
  children,
}) => {
  // The chevron override: local view-state only, never derived state.
  const [override, setOverride] = useState<boolean | null>(null);
  const collapsible = Boolean(strip);
  const expanded =
    !collapsible || (sealed ? defaultExpanded : override ?? defaultExpanded);
  const titleId = `${panelId}-title`;

  const sectionClass = [
    'vpanel',
    active && expanded ? 'vpanel--active' : '',
    expanded ? '' : 'vpanel--collapsed',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={sectionClass} aria-labelledby={titleId} data-testid={testId}>
      {expanded ? (
        <>
          <div className={active ? 'vpanel-head vpanel-head--active' : 'vpanel-head'}>
            <h2 id={titleId} className="vpanel-title">
              {title}
            </h2>
            {subLine && <span className="vpanel-sub">{subLine}</span>}
            {countPill && <span className="vpanel-count">{countPill}</span>}
            {collapsible && !sealed && (
              <button
                type="button"
                className="vpanel-chevron"
                aria-expanded={true}
                aria-label={`Collapse ${title}`}
                onClick={() => setOverride(false)}
              >
                <ChevronDown size={16} aria-hidden="true" />
              </button>
            )}
          </div>
          <div className="vpanel-body">{children}</div>
        </>
      ) : (
        <div className="vpanel-strip">
          {strip?.done ? (
            <CheckCircle2 size={16} className="vpanel-strip-tick" aria-hidden="true" />
          ) : (
            <ChevronRight size={16} className="vpanel-strip-glyph" aria-hidden="true" />
          )}
          <span id={titleId} className="vpanel-strip-label">
            {strip?.label ?? title}
          </span>
          {strip?.summary != null && (
            <>
              <span className="vpanel-strip-dot" aria-hidden="true">
                ·
              </span>
              <span className="vpanel-strip-summary">{strip.summary}</span>
            </>
          )}
          {!sealed && (
            <button
              type="button"
              className="vpanel-strip-link"
              aria-expanded={false}
              aria-label={`Review ${title}`}
              onClick={() => setOverride(true)}
            >
              Review
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default VisionPanel;
